/* operationalSignals.js — calm operational signals — frontend lib.
 *
 * Mirrors `operational_signals_engine.py` so any surface can render
 * the embedded pills without needing the dedicated endpoint when the
 * deal + activity is already in hand. The shared API endpoint
 * (`/api/operational-signals/deal/{id}`) remains the canonical source
 * for production rendering — this fallback compute is for the
 * Mission Control row + Chain Ribbon node where bulk pre-fetching N
 * dedicated calls per row would be wasteful.
 *
 * Doctrine:
 *   - Pure function. No fetch. No state.
 *   - Calm operational vocabulary only.
 *   - Outputs match backend: state ∈ Stable | Monitoring | Needs Reassurance
 *     for seller confidence; Responsive | Slowing | Delayed for
 *     solicitor responsiveness.
 */

// ─── Tone tokens (mirrors backend SELLER_CONFIDENCE_TONE) ────────
export const SELLER_CONFIDENCE_TONE = {
  Stable:              { dot: "#10B981", fg: "#047857", bg: "rgba(16,185,129,0.10)", ring: "rgba(16,185,129,0.18)" },
  Monitoring:          { dot: "#F59E0B", fg: "#B45309", bg: "rgba(245,158,11,0.10)", ring: "rgba(245,158,11,0.18)" },
  "Needs Reassurance": { dot: "#EF4444", fg: "#B91C1C", bg: "rgba(239,68,68,0.10)", ring: "rgba(239,68,68,0.18)" },
};

export const SOLICITOR_RESPONSIVENESS_TONE = {
  Responsive:  { dot: "#10B981", fg: "#047857", bg: "rgba(16,185,129,0.10)", ring: "rgba(16,185,129,0.18)" },
  Slowing:     { dot: "#F59E0B", fg: "#B45309", bg: "rgba(245,158,11,0.10)", ring: "rgba(245,158,11,0.18)" },
  Delayed:     { dot: "#EF4444", fg: "#B91C1C", bg: "rgba(239,68,68,0.10)", ring: "rgba(239,68,68,0.18)" },
};

// ─── Helpers ──────────────────────────────────────────────────────
const _now = () => Date.now();
const _MS_DAY = 86_400_000;

function _parse(v) {
  if (!v) return null;
  if (typeof v === "number") return v;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

function _daysSince(v) {
  const t = _parse(v);
  if (t == null) return null;
  return Math.max(0, Math.floor((_now() - t) / _MS_DAY));
}

function _daysUntil(v) {
  const t = _parse(v);
  if (t == null) return null;
  return Math.floor((t - _now()) / _MS_DAY);
}

function _actKind(row) {
  if (!row) return "";
  return String(row.kind || row.activity_type || row.type || "").toLowerCase();
}

function _actText(row) {
  if (!row) return "";
  return String(row.text || row.title || row.body || "");
}

function _actAt(row) {
  if (!row) return null;
  return _parse(row.at || row.created_at);
}

const CHASE_KEYWORDS = ["chase", "follow up", "follow-up", "nudge", "reminder"];
const ENQUIRY_KEYWORDS = ["enquiry", "enquiries"];

// ═══════════════════════════════════════════════════════════════
//  Seller confidence — frontend mirror of backend logic
// ═══════════════════════════════════════════════════════════════
export function computeSellerConfidence(deal, activities = []) {
  if (!deal) return { state: "Stable", reasons: [], signals: {} };

  const acts = Array.isArray(activities) ? activities : [];

  // Latest activity overall — calm-by-default: 0 when no activity.
  const ats = acts.map(_actAt).filter((t) => t != null);
  const latestAt = ats.length ? Math.max(...ats) : null;
  const silenceDays = latestAt ? Math.max(0, Math.floor((_now() - latestAt) / _MS_DAY)) : 0;

  // Stage stale-ness — same calm default.
  const stageRows = acts.filter((r) => {
    const k = _actKind(r);
    return k === "stage" || k === "stage_change" || k === "stage_advance";
  });
  const stageAts = stageRows.map(_actAt).filter((t) => t != null);
  const lastStageAt = stageAts.length ? Math.max(...stageAts) : null;
  const stalledDays = lastStageAt
    ? Math.max(0, Math.floor((_now() - lastStageAt) / _MS_DAY))
    : 0;

  const txDays = _daysUntil(deal.target_exchange ?? deal.targetExchange);
  const tcDays = _daysUntil(deal.target_completion ?? deal.targetCompletion);
  const overdueExchange = txDays != null && txDays < 0;
  const overdueCompletion = tcDays != null && tcDays < 0;

  const chainHealth = Number(deal.chain_health ?? deal.chainHealth ?? 90);
  const status = String(deal.status || "Active").trim().toLowerCase();
  const atRisk = status === "at risk" || status === "fallen through";

  // Recent chase + enquiry intensity (last 30d)
  const cutoff = _now() - 30 * _MS_DAY;
  const recent = acts.filter((r) => (_actAt(r) ?? cutoff) >= cutoff);
  const chaseCount = recent.filter((r) => {
    const k = _actKind(r);
    const t = _actText(r).toLowerCase();
    return k === "chase" || k === "chase_logged" || CHASE_KEYWORDS.some((kw) => t.includes(kw));
  }).length;
  const enquiryCount = recent.filter((r) => {
    const k = _actKind(r);
    const t = _actText(r).toLowerCase();
    return k === "enquiry" || k === "enquiries_raised" || ENQUIRY_KEYWORDS.some((kw) => t.includes(kw));
  }).length;

  let weight = 0;
  const reasons = [];

  if (silenceDays >= 14)      { weight += 3; reasons.push(`Silent ${silenceDays}d`); }
  else if (silenceDays >= 7)  { weight += 2; reasons.push(`Silent ${silenceDays}d`); }
  else if (silenceDays >= 4)  { weight += 1; }

  if (stalledDays >= 21)      { weight += 3; reasons.push(`Progression stalled ${stalledDays}d`); }
  else if (stalledDays >= 10) { weight += 2; reasons.push(`Progression stalled ${stalledDays}d`); }

  if (overdueExchange)        { weight += 3; reasons.push("Target exchange overdue"); }
  else if (overdueCompletion) { weight += 2; reasons.push("Target completion overdue"); }

  if (atRisk)                 { weight += 3; reasons.push("Deal flagged at risk"); }
  else if (chainHealth < 60)  { weight += 2; reasons.push("Chain health softening"); }
  else if (chainHealth < 75)  { weight += 1; }

  if (chaseCount >= 3)        { weight += 2; reasons.push("Repeated chasing"); }
  else if (chaseCount >= 2)   { weight += 1; }

  if (enquiryCount >= 3)      { weight += 1; reasons.push("Repeated enquiries open"); }

  const stage = String(deal.stage || "").toLowerCase();
  if (stage === "exchanged" || stage === "completed") {
    return { state: "Stable", reasons: [], signals: { silenceDays, stalledDays, chainHealth } };
  }

  let state;
  if (weight >= 5)      state = "Needs Reassurance";
  else if (weight >= 2) state = "Monitoring";
  else                  state = "Stable";

  return {
    state,
    reasons: reasons.slice(0, 3),
    signals: { silenceDays, stalledDays, chainHealth, overdueExchange, overdueCompletion, chaseCount, enquiryCount },
  };
}

// ═══════════════════════════════════════════════════════════════
//  Solicitor responsiveness — used when we have only loose context
//  (e.g. Mission Control synthetic solicitor pairs). For Deal Room
//  we prefer the canonical /api/operational-signals/deal/{id} call.
// ═══════════════════════════════════════════════════════════════
export function computeSolicitorResponsiveness({
  lastInboundAt = null,
  openOverdueCount = 0,
  chaseCount = 0,
  actionRatio = null,
  dealStage = null,
} = {}) {
  if (dealStage && (String(dealStage).toLowerCase() === "exchanged" || String(dealStage).toLowerCase() === "completed")) {
    return { state: "Responsive", reasons: [], signals: {} };
  }
  const daysSilent = _daysSince(lastInboundAt);
  let weight = 0;
  const reasons = [];

  if (daysSilent != null) {
    if (daysSilent >= 7)      { weight += 3; reasons.push(`No reply ${daysSilent}d`); }
    else if (daysSilent >= 4) { weight += 2; reasons.push(`No reply ${daysSilent}d`); }
    else if (daysSilent >= 2) { weight += 1; }
  }

  if (openOverdueCount >= 3)      { weight += 3; reasons.push(`${openOverdueCount} overdue items`); }
  else if (openOverdueCount === 2){ weight += 2; reasons.push("2 overdue items"); }
  else if (openOverdueCount === 1){ weight += 1; }

  if (chaseCount >= 3)            { weight += 2; reasons.push("Repeated chasing"); }
  else if (chaseCount >= 2)       { weight += 1; }

  if (actionRatio != null) {
    if (actionRatio < 0.20)      { weight += 2; reasons.push("Low action follow-through"); }
    else if (actionRatio < 0.45) { weight += 1; }
  }

  let state;
  if (weight >= 4)      state = "Delayed";
  else if (weight >= 2) state = "Slowing";
  else                  state = "Responsive";
  return { state, reasons: reasons.slice(0, 2), signals: { daysSilent, openOverdueCount, chaseCount, actionRatio } };
}
